import { AffiliateLinkOptions } from '../types';
import { ApiClient } from '../utils/api';

interface AffiliateStats {
  clickCount: number;
  conversionCount: number;
  totalEarnings: number;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

/**
 * Service for creating affiliate links
 */
export class AffiliateLinkService {
  private apiClient: ApiClient;
  private gameId: string;
  private referrerAffiliateCode: string | null = null;
  private myAffiliateCode: string | null = null;

  constructor(apiClient: ApiClient, gameId: string) {
    this.apiClient = apiClient;
    this.gameId = gameId;
    console.log('AffiliateLinkService constructor ' + typeof window);
    // Try to extract affiliate code from current URL if we're in a browser
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      this.referrerAffiliateCode = urlParams.get('ref');
      console.log('Referrer affiliate code:', this.referrerAffiliateCode);
    }
  }

  /**
   * Get the affiliate code you arrived with (from query param)
   */
  getReferrerAffiliateCode(): string | null {
    return this.referrerAffiliateCode;
  }

  /**
   * Get the affiliate code for this user (generate if not set)
   * @param userId Required user ID to generate the code
   */
  getMyAffiliateCode(userId: string): string {
    if (!userId) {
      throw new Error('User ID is required to generate an affiliate code');
    }
    // Example: use a hash or a deterministic encoding of the userId
    // For now, use a simple base64url encoding of the userId (could be replaced with a DB lookup or nanoid)
    if (!this.myAffiliateCode) {
      this.myAffiliateCode = btoa(unescape(encodeURIComponent(userId))).replace(/=+$/, '').replace(/\+/g, '-').replace(/\//g, '_');
    }
    return this.myAffiliateCode;
  }

  /**
   * Wrap a URL with your affiliate code
   * @param targetUrl URL to wrap with affiliate tracking
   * @param userId Required user ID to generate the code
   * @returns URL with affiliate tracking parameters
   */
  wrapUrl(targetUrl: string, userId: string): string {
    try {
      // Add https:// protocol if no protocol is specified
      const urlToWrap = targetUrl.match(/^[a-zA-Z]+:\/\//) ? targetUrl : `https://${targetUrl}`;
      const url = new URL(urlToWrap);
      const code = this.getMyAffiliateCode(userId);
      if (code) {
        url.searchParams.set('ref', code);
      }
      return url.toString();
    } catch (error) {
      console.error('Error wrapping URL with affiliate code:', error);
      return targetUrl; // Return original URL if there's an error
    }
  }

  /**
   * Create a new affiliate link for this user
   * @param opts Affiliate link options (must include userId)
   * @returns URL with new affiliate tracking code
   */
  createLink(opts: AffiliateLinkOptions & { userId: string | undefined }): string {
    if (!opts.userId) {
      throw new Error('User ID is required to create an affiliate link');
    }
    try {
      const myCode = this.getMyAffiliateCode(opts.userId);
      const baseUrl = opts.targetUrl || 'https://dev.oncade.xyz';
      return this.wrapUrl(baseUrl, opts.userId);
    } catch (error) {
      console.error('Error creating affiliate link:', error);
      throw error;
    }
  }
} 